import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plane, Plus, MessageCircle, Trash2, Zap, Star } from "lucide-react";

import { adminHeaders, isAdminSession } from "../lib/api";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { kind: "voyageur", name: "", contact: "", route: "", date: "", capacity_kg: "", price_per_kg: "", notes: "" };

export default function GP() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState("voyageur");
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [boosting, setBoosting] = useState(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/gp`, { params: { kind: tab } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  // Poll après retour Stripe boost
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid || !params.get("boosted")) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/gp/boost/status/${sid}`);
        if (data.boosted) { toast.success(T("Annonce boostée pour 7 jours 🚀")); load(); }
      } catch (e) { console.warn("boost status:", e?.message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const boost = async (listing_id) => {
    setBoosting(listing_id);
    try {
      const { data } = await axios.post(`${API}/gp/boost/checkout`, { listing_id, origin_url: window.location.origin }, { headers: adminHeaders() });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || T("Échec checkout.")); setBoosting(null); }
  };

  const submit = async () => {
    if (!form.name || !form.contact || !form.route || !form.date) {
      toast.error(T("Nom, contact, trajet et date requis.")); return;
    }
    try {
      await axios.post(`${API}/gp`, {
        ...form, kind: tab,
        capacity_kg: form.capacity_kg ? Number(form.capacity_kg) : null,
        price_per_kg: form.price_per_kg ? Number(form.price_per_kg) : null,
      });
      toast.success(T("Annonce publiée."));
      setForm(EMPTY); setShowForm(false); load();
    } catch { toast.error(T("Échec — réessaie.")); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/gp/${id}`, { headers: adminHeaders() }); load();
      toast.success(T("Annonce retirée."));
    } catch { toast.error(T("Suppression réservée à l'administrateur.")); }
  };

  const waLink = (it) => `https://wa.me/${it.contact.replace(/[^0-9]/g, "")}?text=` + encodeURIComponent(T("Saalam {name}, j'ai vu ton annonce GP « {route} » sur Papicoo.").replace("{name}", it.name).replace("{route}", it.route));

  return (
    <div data-testid="page-gp" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("GP Sénégal · Diaspora")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Colis & voyageurs.")}<br />
        <span className="italic font-light">{T("Diaspora gagnante.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-4 max-w-3xl leading-relaxed">
        {T("Mise en relation simple, claire, honnête entre")} <strong>{T("voyageurs")}</strong> {T("qui ont des kilos en soute et")} <strong>{T("expéditeurs")}</strong> {T("qui veulent envoyer un colis vers (ou depuis) le Sénégal. Pas de commission cachée. Contact direct par WhatsApp.")}
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        <button data-testid="tab-voyageur" onClick={() => setTab("voyageur")} className={`px-4 py-2 text-sm rounded-full ${tab === "voyageur" ? "papicoo-btn-primary" : "bg-white border border-[color:var(--papicoo-border)]"}`}>
          {T("🛫 Je voyage (j'offre des kilos)")}
        </button>
        <button data-testid="tab-expediteur" onClick={() => setTab("expediteur")} className={`px-4 py-2 text-sm rounded-full ${tab === "expediteur" ? "papicoo-btn-primary" : "bg-white border border-[color:var(--papicoo-border)]"}`}>
          {T("📦 J'expédie (je cherche des kilos)")}
        </button>
        <button data-testid="open-form" onClick={() => setShowForm((s) => !s)} className="px-4 py-2 text-sm rounded-full papicoo-btn-gold inline-flex items-center gap-1">
          <Plus size={14} /> {T("Publier une annonce")}
        </button>
      </div>

      {showForm && (
        <div className="mt-5 papicoo-card p-5 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="gp-name" placeholder={T("Nom / pseudo")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="gp-contact" placeholder={T("WhatsApp (ex : 33612345678)")} value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="gp-route" placeholder={T("Trajet (ex : Paris → Dakar)")} value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="gp-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="gp-kg" type="number" placeholder={T("Kilos disponibles / nécessaires")} value={form.capacity_kg} onChange={(e) => setForm({ ...form, capacity_kg: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
            <input data-testid="gp-price" type="number" placeholder={T("Prix par kilo (€ ou FCFA)")} value={form.price_per_kg} onChange={(e) => setForm({ ...form, price_per_kg: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
          </div>
          <textarea data-testid="gp-notes" rows={2} placeholder={T("Précisions (type de colis accepté, lieu de remise…)")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
          <button data-testid="gp-submit" onClick={submit} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full papicoo-btn-primary text-sm">{T("Publier")}</button>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {items.length === 0 && <div className="papicoo-card p-5 text-sm italic text-[color:var(--papicoo-muted)]">{T("Aucune annonce pour l'instant — sois le premier à publier.")}</div>}
        {items.map((it) => (
          <div key={it.id} data-testid={`gp-item-${it.id}`} className={`papicoo-card p-5 flex flex-wrap items-center gap-4 justify-between ${it.boosted ? "ring-2 ring-[color:var(--papicoo-gold)] bg-gradient-to-br from-amber-50/40 to-white" : ""}`}>
            <div>
              <div className="flex items-center gap-2">
                <Plane size={18} className="text-[color:var(--papicoo-gold)]" />
                <div className="font-display text-xl text-[color:var(--papicoo-ink)]">{it.route}</div>
                {it.boosted && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[color:var(--papicoo-gold)] text-[color:var(--papicoo-ink)]">
                    <Star size={10} /> {T("Boostée")}
                  </span>
                )}
              </div>
              <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">
                {T(it.name)} · {it.date}{it.capacity_kg ? ` · ${it.capacity_kg} kg` : ""}{it.price_per_kg ? ` · ${it.price_per_kg}/kg` : ""}
              </div>
              {it.notes && <div className="text-sm mt-2">{it.notes}</div>}
            </div>
            <div className="flex gap-2 items-center">
              <a href={waLink(it)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full papicoo-btn-gold text-sm">
                <MessageCircle size={14} /> {T("Contacter")}
              </a>
              {isAdminSession() && !it.boosted && (
                <button
                  data-testid={`gp-boost-${it.id}`}
                  onClick={() => boost(it.id)}
                  disabled={boosting === it.id}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] text-xs hover:bg-[color:var(--papicoo-accent)] transition disabled:opacity-60"
                  title={T("Épingler 7 jours en haut de liste")}
                >
                  <Zap size={12} /> {T("Booster 4€")}
                </button>
              )}
              {isAdminSession() && <button data-testid={`gp-remove-${it.id}`} onClick={() => remove(it.id)} className="p-2 rounded-full bg-white border border-[color:var(--papicoo-border)] text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-accent)]" title={T("Retirer")}>
                <Trash2 size={14} />
              </button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
